import React from 'react';
import { createRoot, type Root } from 'react-dom/client';

/**
 * Monte un composant React détaché dans `document.body` et expose une
 * Promise qui se résoud quand le composant appelle `resolve`. Utilisé pour
 * les dialogs impératifs (style SweetAlert) sans casser les call sites.
 */
export function mountDialog<T>(
  render: (props: { resolve: (value: T) => void }) => React.ReactElement,
): Promise<T> {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('mountDialog requires a DOM'));
  }
  const container = document.createElement('div');
  document.body.appendChild(container);
  const root: Root = createRoot(container);
  let unmounted = false;

  return new Promise<T>((resolve) => {
    const cleanup = () => {
      // Démonte après l'animation de close (~250 ms). Si l'environnement DOM a
      // disparu entre-temps (teardown jsdom en test, navigation/HMR très rapide),
      // on abandonne : démonter dans un document détruit est inutile et crashe
      // (react-dom lit `window`, d'où "window is not defined").
      window.setTimeout(() => {
        if (unmounted || typeof window === 'undefined') return;
        unmounted = true;
        root.unmount();
        container.remove();
      }, 250);
    };

    const handle = (value: T) => {
      resolve(value);
      cleanup();
    };

    root.render(render({ resolve: handle }));
  });
}
